import { useEffect, useMemo, useState } from 'react';
import { css } from '@emotion/react';
import {
  PhoneHours,
  PhoneHoursScheduleRule,
  ReadPhoneHoursResponse,
  ScheduleCallRoute,
} from '@weave/schema-gen-ts/dist/schemas/phone/callroute/v1/callroute_service.pb';
import { isEqual } from 'lodash-es';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { usePhoneSettingsShallowStore } from '@frontend/phone-config';
import { breakpoints, useNavSize, useTargetElementContainerQuery } from '@frontend/responsiveness';
import { useNavigationBlocker } from '@frontend/settings-routing';
import { SurfaceHeader } from '@frontend/surface-header';
import { theme } from '@frontend/theme';
import { Tray, useModalControl, Button, useAlert, Heading, TextLink, useTooltip } from '@frontend/design-system';
import { CancelEditModal } from '../../utils/cancel-edit.modal';
import { logError } from '../../utils/loggers';
import { TraySubHeader } from '../tray-sub-header';
import { CalendarToggle } from './calendar-toggle';
import CalendarView from './calendar-view';
import { DayHoursList } from './day-hours-list';
import { usePhoneHoursPermission } from './hooks/use-phone-hours-permission';
import { OverrideNotification } from './phone-hours-form/override-notification';
import { PhoneHoursForm } from './phone-hours-form/phone-hours-form';
import { usePhoneHoursForm } from './phone-hours-form/use-phone-hours-form';
import { usePhoneHoursMutations } from './phone-hours-mutations';
import { SwitchToLargeModal } from './switch-to-large-modal';
import { TimezoneDisplay } from './timezone-display';

interface PhoneHoursTrayProps {
  modalControls: ReturnType<typeof useModalControl>;
  selectedPhoneHour: ReadPhoneHoursResponse;
  mountTarget: string;
  selectedLocationId: string;
  currentPhoneHour: PhoneHours;
  onPreviousClick: () => void;
  onNextClick: () => void;
  isPreviousDisabled: boolean;
  isNextDisabled: boolean;
  isOverrideEnabled: boolean;
  scheduleCallRoutes?: ScheduleCallRoute[];
  initialCalendarViewState?: boolean;
  onCalendarViewChange?: (isOpen: boolean) => void;
  timezone?: string;
  isEditMode: boolean;
  setIsEditMode: (isEditMode: boolean) => void;
}

const SETTINGS_NAV_WIDTH = 248;

export const sortPhoneHoursByScheduleId = (phoneHours: PhoneHoursScheduleRule[]) => {
  return phoneHours
    .slice()
    .sort((a, b) => a.scheduleId.localeCompare(b.scheduleId))
    .map((schedule) => ({
      ...schedule,
      // Sort any nested arrays that might exist in the schedule
      ...(schedule.rules && {
        rules: [...schedule.rules].sort((a, b) => JSON.stringify(a).localeCompare(JSON.stringify(b))),
      }),
    }));
};

export const PhoneHoursTray = ({
  modalControls,
  selectedPhoneHour,
  mountTarget,
  selectedLocationId,
  currentPhoneHour,
  onPreviousClick,
  onNextClick,
  isPreviousDisabled,
  isNextDisabled,
  isOverrideEnabled,
  scheduleCallRoutes,
  initialCalendarViewState,
  onCalendarViewChange,
  timezone,
  isEditMode,
  setIsEditMode,
}: PhoneHoursTrayProps) => {
  const { t } = useTranslation('phone');
  const [showCalendarView, setShowCalendarView] = useState(initialCalendarViewState ?? false);
  const [liveEditingPhoneHours, setLiveEditingPhoneHours] = useState<PhoneHoursScheduleRule[] | null>(null);
  const callRouteName = currentPhoneHour.scheduleCallRoute[0].name;
  const callRouteId = currentPhoneHour.scheduleCallRoute[0].id;

  const mainContentElement = useMemo(() => document.querySelector('#main-content') as HTMLElement, []);
  const [isSmallMode] = useTargetElementContainerQuery({
    maxWidth: breakpoints.xsmall.max,
    element: mainContentElement,
  });
  const [isMediumMode] = useTargetElementContainerQuery({
    maxWidth: breakpoints.medium.max,
    element: mainContentElement,
  });
  const navSize = useNavSize();
  const isSmallerThanDesktop = navSize.isLt('large');
  const shouldUseCompactLayout = (!isSmallerThanDesktop && !isEditMode) || (!isMediumMode && isEditMode);

  const { hasPermission } = usePhoneHoursPermission(selectedLocationId);

  // Form state for editing
  const { settingsTenantLocation } = usePhoneSettingsShallowStore('settingsTenantLocation');
  const phoneTenantId = settingsTenantLocation?.phoneTenantId ?? '';
  const alerts = useAlert();

  const { formProps, getPhoneHours, hasAnyBreakConflict } = usePhoneHoursForm({
    phoneHours: selectedPhoneHour.phoneHours,
    initialName: selectedPhoneHour.name,
  });

  const { update } = usePhoneHoursMutations(phoneTenantId);

  // Calculate derived values for form
  const formCallRouteName = scheduleCallRoutes && scheduleCallRoutes.length > 0 ? scheduleCallRoutes[0].name : '';
  const formCallRouteId = scheduleCallRoutes && scheduleCallRoutes.length > 0 ? scheduleCallRoutes[0].id : '';

  const handleCancelClick = () => {
    formProps.nameForm.getFieldProps('name').onChange({ name: 'name', value: selectedPhoneHour.name });
    formProps.openSchedulesForm.reset();
    handleDoneEditing();
  };

  const hasEmptyBreakName = useMemo(
    () =>
      Object.entries(formProps.breakSchedulesByDay).some(([, breaks]) =>
        breaks.some((breakItem) => breakItem.breakName.trim() === '')
      ),
    [formProps.breakSchedulesByDay]
  );
  const emptyNameField = formProps.nameForm.values.name === '';

  const isSaveDisabled = useMemo(() => {
    const currentValues = {
      name: formProps.nameForm.values.name,
      phoneHours: sortPhoneHoursByScheduleId(getPhoneHours()),
    };

    const initialValues = {
      name: selectedPhoneHour.name,
      phoneHours: sortPhoneHoursByScheduleId(selectedPhoneHour.phoneHours),
    };

    return isEqual(currentValues, initialValues) || emptyNameField;
  }, [
    formProps.nameForm.values.name,
    getPhoneHours,
    selectedPhoneHour.name,
    selectedPhoneHour.phoneHours,
    emptyNameField,
  ]);

  // Modal for unsupported calendar view in small mode
  const unsupportedCalendarModal = useModalControl();
  const cancelEditModal = useModalControl();

  const savePhoneHours = (onSuccess?: () => void) => {
    const nameValue = formProps.nameForm.values.name;
    if (!nameValue) {
      alerts.error(t('Phone Hours must have a name.'));
      return;
    }

    const nextPhoneHours = getPhoneHours();
    setLiveEditingPhoneHours(nextPhoneHours);

    const formValues = {
      id: selectedPhoneHour.id,
      tenantId: phoneTenantId,
      name: nameValue,
      phoneHours: nextPhoneHours,
    };

    update.mutate(formValues, {
      onSuccess: () => {
        alerts.success(t('Phone Hours updated successfully.'));
        handleDoneEditing();
        onSuccess?.();
      },
      onError: () => {
        setLiveEditingPhoneHours(null);
        alerts.error(t('Failed to update Phone Hours.'));
        logError('PhoneHoursEditTray:updateMutation | Failed to update phone hours', {
          data: { phoneTenantId, phoneHoursId: selectedPhoneHour.id },
        });
      },
    });
  };

  const handleSaveClick = () => {
    savePhoneHours();
  };

  // Show edit form when in edit mode
  const shouldShowEditForm = isEditMode;

  const hasUnsavedChanges = shouldShowEditForm && !isSaveDisabled;

  const { proceed } = useNavigationBlocker(
    {
      shouldBlockFn: () => {
        if (hasUnsavedChanges || cancelEditModal.modalProps.show) {
          cancelEditModal.openModal();
          return true;
        }

        return false;
      },
      enableBeforeUnload: true,
    },
    [hasUnsavedChanges, cancelEditModal.modalProps.show]
  );

  const handleTrayCloseRequest = () => {
    if (hasUnsavedChanges) {
      cancelEditModal.openModal();
      return;
    }

    modalControls.closeModal();
  };

  const handleQuitClick = () => {
    cancelEditModal.closeModal();
    proceed();
    modalControls.closeModal();
  };

  const handleSaveAndQuitClick = () => {
    savePhoneHours(() => {
      cancelEditModal.closeModal();
      proceed();
      modalControls.closeModal();
    });
  };

  const handleShowCalendarClick = () => {
    // In smaller view, show unsupported modal instead of opening calendar
    if (isSmallerThanDesktop || (isMediumMode && isEditMode)) {
      unsupportedCalendarModal.openModal();
      return;
    }

    const newState = !showCalendarView;
    setShowCalendarView(newState);
    onCalendarViewChange?.(newState);
  };

  const handleEditClick = () => {
    setIsEditMode(true);
    formProps.openSchedulesForm.reset();
    formProps.nameForm.reset();
  };

  const handleDoneEditing = () => {
    setIsEditMode(false);
  };

  useEffect(() => {
    if (!liveEditingPhoneHours) return;

    // Clear the temporary preview once the persisted prop catches up to the saved value.
    if (
      isEqual(
        sortPhoneHoursByScheduleId(selectedPhoneHour.phoneHours),
        sortPhoneHoursByScheduleId(liveEditingPhoneHours)
      )
    ) {
      setLiveEditingPhoneHours(null);
    }
  }, [selectedPhoneHour.phoneHours, liveEditingPhoneHours]);

  // While editing, render live form values. After save, prefer temporary preview until backend-driven props are refreshed, then revert to the persisted source.
  const calendarPhoneHours = shouldShowEditForm
    ? getPhoneHours()
    : liveEditingPhoneHours ?? selectedPhoneHour.phoneHours;

  if (!modalControls) return null;
  return (
    <>
      <Tray
        mountTarget={mountTarget}
        {...modalControls.modalProps}
        onClose={handleTrayCloseRequest}
        backdropInsetLeft={!isSmallerThanDesktop ? SETTINGS_NAV_WIDTH : undefined}
        width={shouldShowEditForm && !isSmallMode ? 'xlarge' : 'large'}
        css={{
          gap: theme.spacing(2),
          overflowY: 'auto',
          display: 'flex',
          flexDirection: 'column',
          maxWidth: showCalendarView && shouldUseCompactLayout ? `calc(100vw - ${SETTINGS_NAV_WIDTH}px)` : undefined,
          transition: 'max-width 800ms ease',
        }}
      >
        <SurfaceHeader.Root css={{ margin: theme.spacing(-4, -4, 0, -4), width: 'calc(100% + 64px)' }} noBorder>
          <SurfaceHeader.Row>
            <SurfaceHeader.Title>{t('Phone Hours')}</SurfaceHeader.Title>
            <SurfaceHeader.Actions>
              <SurfaceHeader.Icon iconName='x' onClick={handleTrayCloseRequest} />
            </SurfaceHeader.Actions>
          </SurfaceHeader.Row>
          <SurfaceHeader.Body>
            <TraySubHeader
              scheduleCallRoutes={currentPhoneHour?.scheduleCallRoute ?? []}
              selectedLocationId={selectedLocationId}
            />
          </SurfaceHeader.Body>
        </SurfaceHeader.Root>
        <div
          key={selectedPhoneHour.id}
          css={{
            display: 'flex',
            flexDirection: showCalendarView && !isSmallerThanDesktop ? 'row' : 'column',
            gap: isEditMode ? theme.spacing(6) : theme.spacing(4),
            alignItems: showCalendarView && !isSmallerThanDesktop ? 'flex-start' : 'stretch',
          }}
        >
          {showCalendarView && shouldUseCompactLayout && (
            <div css={{ flex: 1, minWidth: 0 }}>
              <div css={{ marginBottom: theme.spacing(7) }}>
                <TextLink
                  disabled={!hasPermission}
                  onClick={handleShowCalendarClick}
                  css={{ display: 'flex', gap: theme.spacing(0.5), width: 'auto', alignItems: 'center' }}
                  weight='bold'
                >
                  <Icon name='collapse-right' color='primary' />
                  {t('Hide Calendar View')}
                </TextLink>
              </div>
              <CalendarView phoneHours={calendarPhoneHours} />
            </div>
          )}

          <div
            css={{
              flex: showCalendarView && !isSmallerThanDesktop ? '0 0 400px' : '1',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              gap: theme.spacing(5),
            }}
          >
            <div
              css={{
                display: 'flex',
                flexDirection: 'column',
                gap: isOverrideEnabled ? theme.spacing(3.5) : isSmallMode ? theme.spacing(1) : theme.spacing(3.5),
              }}
            >
              <PhoneHoursHeader
                phoneHourId={selectedPhoneHour.id}
                onEditClick={handleEditClick}
                onShowCalendarClick={handleShowCalendarClick}
                showCalendarView={showCalendarView}
                selectedLocationId={selectedLocationId}
                isEditMode={shouldShowEditForm}
                onDoneEditing={handleDoneEditing}
                timezone={timezone}
                isSmallMode={isSmallMode}
                isSmallerThanDesktop={isSmallerThanDesktop}
                isMediumMode={isMediumMode}
                shouldUseCompactLayout={shouldUseCompactLayout}
                isSaveDisabled={isSaveDisabled}
                handleSaveClick={handleSaveClick}
              />
              {isOverrideEnabled && !shouldShowEditForm && (
                <OverrideNotification callRouteName={callRouteName} callRouteId={callRouteId} />
              )}
              {shouldShowEditForm ? (
                <EditFormContent
                  isOverrideEnabled={isOverrideEnabled}
                  callRouteName={formCallRouteName}
                  callRouteId={formCallRouteId}
                  formProps={formProps}
                  handleSaveClick={handleSaveClick}
                  handleCancelClick={handleCancelClick}
                  isSaveDisabled={isSaveDisabled}
                  hasEmptyBreakName={hasEmptyBreakName}
                  emptyNameField={emptyNameField}
                  hasAnyBreakConflict={hasAnyBreakConflict}
                  isSmallMode={isSmallMode}
                />
              ) : (
                <div>
                  {isSmallMode && (
                    <Heading level={3} css={{ marginBottom: theme.spacing(2) }}>
                      {t('Hours')}
                    </Heading>
                  )}

                  <DayHoursList
                    phoneHours={selectedPhoneHour.phoneHours}
                    useLargerFonts={true}
                    isSmallMode={isSmallMode}
                  />
                </div>
              )}
            </div>
            {!shouldShowEditForm && (
              <div
                css={{
                  display: 'flex',
                  gap: theme.spacing(2),
                  justifyContent: 'flex-end',
                }}
              >
                <Button variant='secondary' size='large' onClick={onPreviousClick} disabled={isPreviousDisabled}>
                  {t('Previous')}
                </Button>
                <Button variant='secondary' size='large' onClick={onNextClick} disabled={isNextDisabled}>
                  {t('Next')}
                </Button>
              </div>
            )}
          </div>
        </div>
      </Tray>

      {/* Unsupported Calendar View Modal */}
      <SwitchToLargeModal {...unsupportedCalendarModal.modalProps} />
      <CancelEditModal
        modalProps={cancelEditModal.modalProps}
        disabled={isSaveDisabled || hasEmptyBreakName || emptyNameField || hasAnyBreakConflict}
        onQuitClick={handleQuitClick}
        onSaveAndQuitClick={handleSaveAndQuitClick}
      />
    </>
  );
};

const PhoneHoursHeader = ({
  phoneHourId,
  onEditClick,
  onShowCalendarClick,
  showCalendarView,
  selectedLocationId,
  isSmallerThanDesktop,
  shouldUseCompactLayout,
  isEditMode,
  isMediumMode,
  onDoneEditing,
  timezone,
  isSmallMode,
  isSaveDisabled,
  handleSaveClick,
}: {
  phoneHourId: string;
  onEditClick: () => void;
  onShowCalendarClick: () => void;
  showCalendarView: boolean;
  selectedLocationId: string;
  isEditMode?: boolean;
  onDoneEditing?: () => void;
  timezone?: string;
  isSmallMode?: boolean;
  isSmallerThanDesktop?: boolean;
  isMediumMode?: boolean;
  shouldUseCompactLayout?: boolean;
  isSaveDisabled?: boolean;
  handleSaveClick?: () => void;
}) => {
  const { t } = useTranslation('phone');
  const { tooltipProps, triggerProps, Tooltip } = useTooltip();

  const { hasPermission } = usePhoneHoursPermission(selectedLocationId);

  return (
    <div
      css={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: theme.spacing(1),
        width: '100%',
        flexDirection: 'column',
      }}
    >
      {phoneHourId && (
        <div
          {...triggerProps}
          css={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            width: '100%',
          }}
        >
          <div css={{ display: 'flex', alignItems: 'center', gap: theme.spacing(2) }}>
            {!showCalendarView && (
              <CalendarToggle
                showCalendarView={showCalendarView}
                onToggle={onShowCalendarClick}
                hasPermission={hasPermission}
                isSmallMode={isSmallerThanDesktop}
              />
            )}
          </div>
          {isEditMode ? (
            <div
              css={{
                display: 'flex',
                flexDirection: showCalendarView && !shouldUseCompactLayout ? 'row' : 'column',
                alignItems: 'flex-end',
                justifyContent: showCalendarView && !shouldUseCompactLayout ? 'space-between' : 'flex-end',
                gap: theme.spacing(0.5),
                width: '100%',
              }}
            >
              {showCalendarView && !shouldUseCompactLayout && (
                <CalendarToggle
                  showCalendarView={showCalendarView}
                  onToggle={onShowCalendarClick}
                  hasPermission={hasPermission}
                  isSmallMode={isMediumMode}
                />
              )}
              <TextLink
                disabled={!hasPermission}
                onClick={isSaveDisabled ? onDoneEditing : handleSaveClick}
                css={{ display: 'flex', gap: theme.spacing(0.5), width: 'auto', alignItems: 'center' }}
                weight='bold'
              >
                {isSaveDisabled ? t('View Hours') : t('Save Hours')}
              </TextLink>
            </div>
          ) : (
            <div
              css={{
                ...(isSmallMode
                  ? {
                      display: 'flex',
                      flexDirection: 'row',
                      width: '100%',
                      justifyContent: 'space-between',
                    }
                  : {}),

                ...(isSmallerThanDesktop && showCalendarView
                  ? {
                      display: 'flex',
                      width: '100%',
                      justifyContent: 'space-between',
                    }
                  : {}),
              }}
            >
              {showCalendarView && isSmallerThanDesktop && (
                <CalendarToggle
                  showCalendarView={showCalendarView}
                  onToggle={onShowCalendarClick}
                  hasPermission={hasPermission}
                  isSmallMode={isSmallerThanDesktop}
                />
              )}
              <TextLink
                disabled={!hasPermission}
                onClick={onEditClick}
                css={{
                  display: 'flex',
                  gap: theme.spacing(0.5),
                  width: 'auto',
                  alignItems: 'center',
                  marginLeft: 'auto',
                }}
                weight='bold'
              >
                {t('Edit Hours')}
              </TextLink>
            </div>
          )}
          {!hasPermission && (
            <Tooltip {...tooltipProps}>
              {t('You do not have the necessary permissions to make changes to this setting.')}
            </Tooltip>
          )}
        </div>
      )}
      <TimezoneDisplay timezone={timezone} />
    </div>
  );
};

const EditFormContent = ({
  isOverrideEnabled,
  callRouteName,
  callRouteId,
  formProps,
  handleSaveClick,
  handleCancelClick,
  isSaveDisabled,
  hasEmptyBreakName,
  emptyNameField,
  hasAnyBreakConflict,
  isSmallMode,
}: {
  isOverrideEnabled: boolean;
  callRouteName: string;
  callRouteId: string;
  formProps: ReturnType<typeof usePhoneHoursForm>['formProps'];
  handleSaveClick: () => void;
  handleCancelClick: () => void;
  isSaveDisabled: boolean;
  hasEmptyBreakName: boolean;
  emptyNameField: boolean;
  hasAnyBreakConflict: boolean;
  isSmallMode?: boolean;
}) => {
  const { t } = useTranslation('phone');

  return (
    <div css={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(2) }}>
      {isOverrideEnabled && <OverrideNotification callRouteName={callRouteName} callRouteId={callRouteId} />}
      <PhoneHoursForm {...formProps} isSmallMode={isSmallMode} />

      <div
        css={css`
          display: flex;
          justify-content: flex-end;
          gap: ${theme.spacing(2)};
          margin-top: ${theme.spacing(4)};
        `}
      >
        <Button variant='secondary' size='large' onClick={handleCancelClick}>
          {t('Cancel')}
        </Button>
        <Button
          onClick={handleSaveClick}
          size='large'
          trackingId='phone::setting::phone-hours:save-hours-btn'
          disabled={isSaveDisabled || hasEmptyBreakName || emptyNameField || hasAnyBreakConflict}
        >
          {t('Save')}
        </Button>
      </div>
    </div>
  );
};
